import { Middleware } from "@reduxjs/toolkit";
import {
  showGlobalMessage,
  incrementLoader,
  decrementLoader,
} from "./globalMessagesSlice";
import {
  GlobalMessagesConfig,
  IGlobalMessage,
  ShowOnRule,
} from "./globalMessages.types";

type ThunkLifecycle = "pending" | "fulfilled" | "rejected";

/**
 * Detects thunk lifecycle by checking action type suffix.
 */
function getThunkLifecycle(actionType: string): ThunkLifecycle | null {
  if (actionType.endsWith("/pending")) return "pending";
  if (actionType.endsWith("/fulfilled")) return "fulfilled";
  if (actionType.endsWith("/rejected")) return "rejected";
  return null;
}

/**
 * Safely extracts globalMessagesConfig from action.meta.arg.
 */
function getGlobalMessagesConfig(
  action: any
): GlobalMessagesConfig | undefined {
  return action?.meta?.arg?.globalMessagesConfig;
}

/**
 * Finds the first ShowOnRule matching the given type.
 */
function findShowOnRule(
  config: GlobalMessagesConfig,
  type: "success" | "error"
): ShowOnRule | undefined {
  return config.showOn.find((rule) => rule.type === type);
}

/**
 * Maps config variant to the displayAs value used in IGlobalMessage.
 */
function mapVariantToDisplayAs(variant: "toast" | "dialog"): "snackbar" | "dialog" {
  return variant === "toast" ? "snackbar" : "dialog";
}

const DEDUP_WINDOW_MS = 2000;

/**
 * Dispatches showGlobalMessage with deduplication.
 * Skips dispatch if an identical message (same type + message text) was
 * dispatched within the last 2000ms.
 */
function dispatchWithDedup(
  storeAPI: any,
  recentMessages: Map<string, number>,
  message: IGlobalMessage
): void {
  const now = Date.now();
  const key = `${message.type}:${message.message}`;

  // Clean up expired entries
  recentMessages.forEach((timestamp, k) => {
    if (now - timestamp > DEDUP_WINDOW_MS) {
      recentMessages.delete(k);
    }
  });

  // Check for duplicate
  const lastDispatched = recentMessages.get(key);
  if (lastDispatched !== undefined && now - lastDispatched < DEDUP_WINDOW_MS) {
    return; // Skip duplicate
  }

  recentMessages.set(key, now);
  storeAPI.dispatch(showGlobalMessage(message));
}

/**
 * Handles manual rejection (rejectWithValue with type: "manual").
 * Priority: payload.message → config error rule → default message.
 */
function handleManualRejection(
  storeAPI: any,
  recentMessages: Map<string, number>,
  action: any,
  config: GlobalMessagesConfig | undefined
): void {
  const payload = action.payload;

  if (payload?.message) {
    // Use payload message with payload variant or default to snackbar
    const displayAs: "snackbar" | "dialog" =
      payload.variant === "dialog" ? "dialog" : "snackbar";
    dispatchWithDedup(storeAPI, recentMessages, {
      type: "error",
      message: payload.message,
      displayAs,
    });
  } else if (config) {
    const errorRule = findShowOnRule(config, "error");
    if (errorRule) {
      dispatchWithDedup(storeAPI, recentMessages, {
        type: "error",
        message: errorRule.message,
        displayAs: mapVariantToDisplayAs(errorRule.variant),
      });
    } else {
      dispatchWithDedup(storeAPI, recentMessages, {
        type: "error",
        message: "An error occurred",
        displayAs: "snackbar",
      });
    }
  } else {
    dispatchWithDedup(storeAPI, recentMessages, {
      type: "error",
      message: "An error occurred",
      displayAs: "snackbar",
    });
  }
}

/**
 * Handles silent rejection (rejectWithValue without type: "manual").
 * If config has an error ShowOnRule, dispatch that message.
 * If no config but payload has a message, show it as an error toast.
 * Otherwise does nothing.
 */
function handleSilentRejection(
  storeAPI: any,
  recentMessages: Map<string, number>,
  action: any,
  config: GlobalMessagesConfig | undefined
): void {
  if (config) {
    const errorRule = findShowOnRule(config, "error");
    if (errorRule) {
      dispatchWithDedup(storeAPI, recentMessages, {
        type: "error",
        message: errorRule.message,
        displayAs: mapVariantToDisplayAs(errorRule.variant),
      });
    }
    return;
  }

  // No config — if payload has a message, show it as error toast
  const payloadMessage = action.payload?.message;
  if (payloadMessage) {
    dispatchWithDedup(storeAPI, recentMessages, {
      type: "error",
      message: payloadMessage,
      displayAs: "snackbar",
    });
  }
}

/**
 * Redux middleware that intercepts createAsyncThunk lifecycle actions
 * and dispatches global message/loader actions based on globalMessagesConfig.
 */
export const globalMessagesMiddleware: Middleware = (storeAPI) => {
  const recentMessages = new Map<string, number>();

  return (next) => (action: any) => {
    const result = next(action);
    const lifecycle = getThunkLifecycle(action.type);
    if (!lifecycle) return result;

    const config = getGlobalMessagesConfig(action);

    // --- LOADER HANDLING ---
    if (config?.showLoader) {
      if (lifecycle === "pending") {
        storeAPI.dispatch(incrementLoader());
      } else {
        // fulfilled or rejected
        storeAPI.dispatch(decrementLoader());
      }
    }

    // --- MESSAGE HANDLING ---
    if (lifecycle === "rejected") {
      const isRejectedWithValue = action.meta?.rejectedWithValue === true;
      const isManual =
        isRejectedWithValue && action.payload?.type === "manual";
      const isUnhandled = !isRejectedWithValue;
      const isSilentReject =
        isRejectedWithValue && action.payload?.type !== "manual";

      if (isUnhandled) {
        // Case 1: Thunk threw without rejectWithValue — always show error toast
        dispatchWithDedup(storeAPI, recentMessages, {
          type: "error",
          message: action.error?.message || "An unexpected error occurred",
          displayAs: "snackbar",
        });
      } else if (isManual) {
        // Case 2: rejectWithValue({ type: "manual", message?: "..." })
        handleManualRejection(storeAPI, recentMessages, action, config);
      } else if (isSilentReject) {
        // Case 3: rejectWithValue(payload) WITHOUT type: "manual"
        handleSilentRejection(storeAPI, recentMessages, action, config);
      }
    }

    if (lifecycle === "fulfilled" && config) {
      const successRule = findShowOnRule(config, "success");
      if (successRule) {
        dispatchWithDedup(storeAPI, recentMessages, {
          type: "success",
          message: successRule.message,
          displayAs: mapVariantToDisplayAs(successRule.variant),
        });
      }
    }

    return result;
  };
};
